import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { setPermissionDeniedPath } from "@/features/auth/authSlice";

/** Fixed id so repeated server errors collapse into one toast instead of stacking. */
const SERVER_ERROR_TOAST_ID = "server-error";

export function getErrorMessage(error, fallback = "Something went wrong. Please try again.") {
  return (
    error?.data?.Message ??
    error?.data?.title ??
    error?.data?.message ??
    (typeof error?.error === "string" ? error.error : null) ??
    error?.message ??
    fallback
  );
}

/**
 * Turns an RTK Query error into the right piece of UI.
 *
 * The old version tracked "have I already shown the 500 toast?" in a mutable
 * module-level boolean that every component shared and nothing reliably reset.
 * A fixed toast id gives the same de-duplication without the shared mutable state.
 */
export function useErrorToast() {
  const dispatch = useDispatch();
  const location = useLocation();

  return useCallback(
    (error) => {
      if (!error) return;

      // A 403 is a routing concern, not a notification: the page itself swaps
      // to the access-denied view.
      if (error.status === 403) {
        dispatch(setPermissionDeniedPath(location.pathname));
        return;
      }

      // 401 is already handled by the base query (refresh, then logout).
      if (error.status === 401) return;

      if (error.status === 500 || error.status === "FETCH_ERROR") {
        toast.error("Can't reach the server", {
          id: SERVER_ERROR_TOAST_ID,
          description: "The request failed. Check your connection and try again.",
          duration: 8000,
        });
        return;
      }

      toast.error(getErrorMessage(error));
    },
    [dispatch, location.pathname],
  );
}
